import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../../env";
import MTable from "../../../components/mtable";
import Loading from "../../../components/loading";
import ErrorFrame from "../../../components/error";

const TAB = { ACTIVE: "active", INACTIVE: "inactive" };

// Dates are bucketed by month in UTC on the backend, so display them in UTC too.
// Otherwise e.g. 31 Aug 23:00 UTC would show as 1 Sep in Serbia while counting toward August.
export const formatDate = (date) =>
    new Date(date).toLocaleDateString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
        timeZone: "UTC",
    });

export const formatMonth = (month) =>
    new Date(`${month}-01T00:00:00Z`).toLocaleDateString(undefined, {
        month: "long",
        year: "numeric",
        timeZone: "UTC",
    });

export const formatName = (person) =>
    `${person.firstName ?? ""} ${person.lastName ?? ""}`.trim() || person.email;

export const renderPerson = (person) => (
    <div>
        <p>{formatName(person)}</p>
        <p className="card-label neutral">{person.email}</p>
    </div>
);

const pairColumns = [
    { label: "Mentee", render: (pair) => renderPerson(pair.mentee) },
    { label: "Mentor", render: (pair) => renderPerson(pair.mentor) },
    { label: "Track", value: (pair) => pair.trackName ?? "-" },
];

export default function MonthDrilldown({
    programId,
    endpoint,
    month,
    startDate,
    endDate,
    config,
    onClose,
}) {
    const [data, setData] = useState(null);
    const [state, setState] = useState("loading");
    const [tab, setTab] = useState(TAB.ACTIVE);
    const [retryToken, setRetryToken] = useState(0);
    const panelRef = useRef(null);

    useEffect(() => {
        setState("loading");
        setTab(TAB.ACTIVE);
        const controller = new AbortController();
        api.get(`programs/${programId}/timeseries/${endpoint}/${month}`, {
            signal: controller.signal,
            params: { startDate, endDate },
        })
            .then((res) => {
                setData(res.data);
                setState("showing");
            })
            .catch(() => {
                if (!controller.signal.aborted) setState("error");
            });
        return () => controller.abort();
    }, [programId, endpoint, month, startDate, endDate, retryToken]);

    useEffect(() => {
        panelRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
        });
    }, [month]);

    const activePairs = useMemo(
        () => data?.pairs.filter((p) => p[config.itemsKey].length > 0) ?? [],
        [data, config],
    );
    const inactivePairs = useMemo(
        () => data?.pairs.filter((p) => p[config.itemsKey].length === 0) ?? [],
        [data, config],
    );

    const activeMapper = useMemo(
        () => ({ columns: [...pairColumns, config.itemsColumn] }),
        [config],
    );
    const inactiveMapper = useMemo(() => ({ columns: pairColumns }), []);

    const ratePercent = data ? Math.round(data.rate * 1000) / 10 : 0;

    return (
        <div ref={panelRef} className="vi-spacer-s">
            <div className="flex center space-between gap-xs v-spacer-xs">
                <p className="section-title-s">{formatMonth(month)}</p>
                <button className="with-icon icon outline" onClick={onClose}>
                    <span className="material-symbols-outlined scale-s">
                        close
                    </span>
                </button>
            </div>

            {state === "loading" && <Loading />}
            {state === "error" && (
                <ErrorFrame onAction={() => setRetryToken((t) => t + 1)} />
            )}
            {state === "showing" && (
                <>
                    <p className="v-spacer-xs">
                        {data[config.countKey]} of {data.eligiblePairs} pairs{" "}
                        {config.summary} ({ratePercent}%)
                    </p>
                    <div className="flex gap-xs v-spacer-xs">
                        <button
                            className={tab === TAB.ACTIVE ? "solid" : "outline"}
                            onClick={() => setTab(TAB.ACTIVE)}
                        >
                            {config.activeTabLabel} ({activePairs.length})
                        </button>
                        <button
                            className={
                                tab === TAB.INACTIVE ? "solid" : "outline"
                            }
                            onClick={() => setTab(TAB.INACTIVE)}
                        >
                            {config.inactiveTabLabel} ({inactivePairs.length})
                        </button>
                    </div>
                    {tab === TAB.ACTIVE ? (
                        <MTable rawData={activePairs} mapper={activeMapper} />
                    ) : (
                        <MTable
                            rawData={inactivePairs}
                            mapper={inactiveMapper}
                        />
                    )}
                </>
            )}
        </div>
    );
}
